import {
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    List,
    ListItem,
    ListItemText,
    Typography,
    Box,
    CircularProgress,
} from "@mui/material";
import { makeStyles } from "@material-ui/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import CommonServiceApi from "../../services/common-service";
import SnackbarDisplay from "../../components/SnackbarDisplay";
import { SnackbarDisplayProps } from "../../components/interface";
import DialogDisplay from "../../components/DialogDisplay";
import LoadingModal from "../../components/LoadingModal";

const useStyles = makeStyles({
    container: {
        marginTop: "30px",
        marginBottom: "30px",
        padding: "20px",
        boxShadow: "1px 1px 5px black",
    },
    searchSection: {
        marginBottom: "20px",
    },
    userList: {
        maxHeight: "200px",
        overflowY: "auto",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "10px",
    },
    permissionsSection: {
        marginTop: "20px",
        padding: "15px",
        border: "1px solid #ddd",
        borderRadius: "4px",
    },
    permissionItem: {
        marginBottom: "10px",
    },
});

interface User {
    id: number;
    firstName?: string;
    lastName?: string;
    username?: string;
    email?: string;
    displayName?: string;
}

interface UserPermissions {
    userCanAddTicket: boolean;
    allowAddVisitFromField: boolean;
    allowAddJobTypeFromField: boolean;
    isFieldManager: boolean;
    showScheduler: boolean;
}

const UserPermissionsComponent = ({ selectedCompany }: { selectedCompany: any }) => {
    const classes = useStyles();
    const apiservices = useMemo(() => new CommonServiceApi(), []);
    
    const [searchQuery, setSearchQuery] = useState("");
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userSettingsId, setUserSettingsId] = useState<string | null>(null);
    const [permissions, setPermissions] = useState<UserPermissions>({
        userCanAddTicket: false,
        allowAddVisitFromField: false,
        allowAddJobTypeFromField: false,
        isFieldManager: false,
        showScheduler: false,
    });
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [alertState, setAlertState] = useState<SnackbarDisplayProps["alertState"]>("");
    const [alertData, setAlertData] = useState("");
    const [dialogOpenState, setDialogOpenState] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");

    const resetUserSelection = useCallback(() => {
        setSelectedUser(null);
        setUserSettingsId(null);
        setUsers([]);
        setPermissions({
            userCanAddTicket: false,
            allowAddVisitFromField: false,
            allowAddJobTypeFromField: false,
            isFieldManager: false,
            showScheduler: false,
        });
    }, []);

    const searchUsers = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setUsers([]);
            return;
        }
        if (!selectedCompany || !selectedCompany.value) {
            setAlertState("error");
            setAlertData("Please select a company first");
            setTimeout(() => setAlertState(""), 6000);
            return;
        }
        try {
            setSearching(true);
            const result = await apiservices.searchUsers(query);
            setUsers(Array.isArray(result) ? result : []);
        } catch (err: any) {
            console.error(err);
            setAlertState("error");
            setAlertData("Failed to search users");
            setTimeout(() => setAlertState(""), 6000);
            setUsers([]);
        } finally {
            setSearching(false);
        }
    }, [selectedCompany, apiservices]);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchQuery(value);
        if (value.trim().length === 0) {
            resetUserSelection();
            return;
        }
        if (value.length >= 2) {
            searchUsers(value);
        } else {
            setUsers([]);
        }
    }, [searchUsers, resetUserSelection]);

    const handleUserSelect = useCallback(async (user: User) => {
        setSelectedUser(user);
        setSearchQuery(user.displayName || user.username || `${user.firstName} ${user.lastName}`.trim() || user.email || "");
        setUsers([]);
        setLoading(true);
        try {
            const userSettings = await apiservices.getUserPermissions(user.id);
            
            if (Array.isArray(userSettings) && userSettings.length > 0 && userSettings[0].id) {
                const userSettingsData = userSettings[0];
                // User settings exist - store the ID and permissions
                setUserSettingsId(userSettingsData.id);
                setPermissions({
                    userCanAddTicket: userSettingsData.userCanAddTicket || false,
                    allowAddVisitFromField: userSettingsData.allowAddVisitFromField || false,
                    allowAddJobTypeFromField: userSettingsData.allowAddJobTypeFromField || false,
                    isFieldManager: userSettingsData.isFieldManager || false,
                    showScheduler: userSettingsData.showScheduler || false,
                });
            } else {
                // User settings don't exist yet - initialize with default values
                // We'll create them when updating
                setUserSettingsId(null);
                setPermissions({
                    userCanAddTicket: false,
                    allowAddVisitFromField: false,
                    allowAddJobTypeFromField: false,
                    isFieldManager: false,
                    showScheduler: false,
                });
            }
        } catch (err: any) {
            console.error(err);
            setAlertState("error");
            setAlertData("Failed to load user permissions");
            setTimeout(() => setAlertState(""), 6000);
            setUserSettingsId(null);
        } finally {
            setLoading(false);
        }
    }, [apiservices]);

    const handlePermissionChange = useCallback((field: keyof UserPermissions) => {
        setPermissions((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    }, []);

    const handleUpdate = useCallback(() => {
        if (!selectedUser) {
            setAlertState("error");
            setAlertData("Please select a user first");
            setTimeout(() => setAlertState(""), 6000);
            return;
        }
        setDialogOpenState(true);
        setDialogMessage(`Are you sure you want to update permissions for ${selectedUser.displayName || selectedUser.username || selectedUser.email}?`);
    }, [selectedUser]);

    const confirmUpdate = useCallback(async () => {
        if (!selectedUser) {
            return;
        }
        setLoading(true);
        setDialogOpenState(false);
        try {
            // Prepare update payload
            // If userSettingsId exists, include it for update
            // If not, the API will create new settings
            const updatePayload: any = {
                userId: selectedUser.id, // This is the actual user ID
                ...permissions,
            };
            
            // Only include id if user settings already exist
            if (userSettingsId) {
                updatePayload.id = userSettingsId; // This is the user settings record ID
            }
            
            await apiservices.updateUserPermissions(updatePayload);
            
            // Reload user settings to get the new ID if it was created
            if (!userSettingsId) {
                const updatedSettings = await apiservices.getUserPermissions(selectedUser.id);
                if (updatedSettings && updatedSettings.id) {
                    setUserSettingsId(updatedSettings.id);
                }
            }
            
            setAlertState("success");
            setAlertData("User permissions updated successfully");
            setTimeout(() => setAlertState(""), 6000);
        } catch (err: any) {
            console.error(err);
            setAlertState("error");
            setAlertData("Failed to update user permissions");
            setTimeout(() => setAlertState(""), 6000);
        } finally {
            setLoading(false);
        }
    }, [selectedUser, userSettingsId, permissions, apiservices]);

    useEffect(() => {
        // Reset when company changes
        setSearchQuery("");
        resetUserSelection();
    }, [selectedCompany, resetUserSelection]);

    return (
        <Paper className={classes.container}>
            <Typography variant="h5" gutterBottom>
                User Level Permissions
            </Typography>
            
            <Box className={classes.searchSection}>
                <TextField
                    fullWidth
                    label="Search User by Name or Email"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Type at least 2 characters to search..."
                    disabled={!selectedCompany || !selectedCompany.value}
                />
                {searching && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {users.length > 0 && (
                    <Paper className={classes.userList}>
                        <List>
                            {users.map((user) => (
                                <ListItem
                                    key={user.id}
                                    button
                                    onClick={() => handleUserSelect(user)}
                                >
                                    <ListItemText
                                        primary={user.displayName || user.username || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                                        secondary={user.email}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}
                {!searching &&
                    users.length === 0 &&
                    searchQuery.length >= 2 &&
                    !selectedUser && (
                        <Box mt={2}>
                            <Typography variant="body2" color="textSecondary">
                                No users found.
                            </Typography>
                        </Box>
                    )}
            </Box>

            {selectedUser && (
                <Box className={classes.permissionsSection}>
                    <Typography variant="h6" gutterBottom>
                        Permissions for: {selectedUser.displayName || selectedUser.username || selectedUser.email}
                    </Typography>
                    
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={permissions.userCanAddTicket}
                                onChange={() => handlePermissionChange("userCanAddTicket")}
                            />
                        }
                        label="Allow user to create ticket from field apps"
                        className={classes.permissionItem}
                    />
                    
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={permissions.allowAddVisitFromField}
                                onChange={() => handlePermissionChange("allowAddVisitFromField")}
                            />
                        }
                        label="Allow user to add/edit visit from field apps"
                        className={classes.permissionItem}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={permissions.allowAddJobTypeFromField}
                                onChange={() => handlePermissionChange("allowAddJobTypeFromField")}
                            />
                        }
                        label="Allow user to add job type from field apps"
                        className={classes.permissionItem}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={permissions.isFieldManager}
                                onChange={() => handlePermissionChange("isFieldManager")}
                            />
                        }
                        label="Allow user to view all jobs from field apps"
                        className={classes.permissionItem}
                    />
                    
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={permissions.showScheduler}
                                onChange={() => handlePermissionChange("showScheduler")}
                            />
                        }
                        label="Allow user to view scheduler"
                        className={classes.permissionItem}
                    />
                    
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleUpdate}
                        style={{ marginTop: "15px" }}
                    >
                        Update Permissions
                    </Button>
                </Box>
            )}

            <DialogDisplay
                dialogOpenState={dialogOpenState}
                dialogTitle="Confirm Update"
                dialogMessage={dialogMessage}
                updateDialogMessage={(message) => {
                    setDialogMessage(message);
                }}
                confirm={confirmUpdate}
                setDialogOpenState={setDialogOpenState}
            />
            
            <SnackbarDisplay
                alertState={alertState}
                alertData={alertData}
                setAlertState={setAlertState}
            />
            
            <LoadingModal openState={loading} />
        </Paper>
    );
};

export default UserPermissionsComponent;
